import { Box, styled, Typography, Tooltip } from '@mui/material'
import { formatConsumptionValue } from '@/utils/consumptionFormat'

type ChartDataItem = {
  value: number
  date: string
}

type MiniChartProps = {
  data: ChartDataItem[]
}

const StyledTooltipText = styled(Typography)({
  color: '#EFF2F7',
  textAlign: 'center',
  fontFamily: 'Rubik',
  fontSize: 12,
  fontStyle: 'normal',
  fontWeight: 300,
  lineHeight: '120%',
})

const CustomTooltipContent = ({ value, date }: { value: number, date: string }) => (
  <Box
    sx={{
      display: 'flex',
      borderRadius: '2px',
      background: 'rgba(0, 63, 97, 0.80)',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        borderRight: '1px solid #EFF2F7',
        padding: '3px',
      }}
    >
      <Box>
        <StyledTooltipText>ק׳</StyledTooltipText>
      </Box>
      <Box>
        <StyledTooltipText>{formatConsumptionValue(value)}</StyledTooltipText>
      </Box>
    </Box>
    <Box
      sx={{
        padding: '3px',
      }}
    >
      <StyledTooltipText>{date}</StyledTooltipText>
    </Box>
  </Box>
)

export default function MiniChart({ data }: MiniChartProps) {
  if (!Array.isArray(data) || data.length === 0) return null

  // Limit to 7 items for display
  const fixedData = data.slice(0, 7)
  
  // Pad with empty items if less than 7
  while (fixedData.length < 7) {
    fixedData.push({ value: 0, date: '' })
  }

  // Find max value for scaling (avoid division by zero if all are 0)
  const maxValue = Math.max(...fixedData.map(d => d.value), 1)

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end', // Ensure bars align to bottom
        justifyContent: 'center',
        gap: '4px', // Space between bars
        height: 35,
        width: 140,
        paddingBottom: '1px' // Small padding from bottom edge
      }}
    >
      {fixedData.map((item, index) => {
        // Calculate height percentage (min 4px for visibility of "empty" bars)
        const isZero = item.value === 0
        const heightPercent = isZero 
          ? '4px' 
          : `${Math.max((item.value / maxValue) * 100, 10)}%`
        
        return (
          <Tooltip 
            key={index} 
            title={<CustomTooltipContent value={item.value} date={item.date} />}
            placement="top"
            arrow
          >
            <Box
              sx={{
                width: '12px', // Fixed bar width
                height: heightPercent,
                backgroundColor: isZero ? '#1D325A' : '#71ACFF',
                // borderRadius: '2px',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: isZero ? '#2A4A80' : '#1D4E89', // Darker blue on hover
                }
              }}
            />
          </Tooltip>
        )
      })}
    </Box>
  )
}
